<template>
  <div
    :class="
      journey.progress < 100
        ? 'journey-card journey-card--in-progress'
        : 'journey-card'
    "
  >
    <div class="journey-card--wrap">
      <div class="journey-card__name">
        <p class="journey-card__name--title">
          {{ journey.title }}
        </p>
        <span class="journey-card__time">
          {{ daysCount }} {{ daysCount === 1 ? 'day' : 'days' }}
        </span>
      </div>
      <div class="journey-card__statistics">
        <p class="journey-card__statistics__item">
          <span>Positive actions:</span>
          {{ journey.positiveActions || 0 }}
        </p>
        <p class="journey-card__statistics__item">
          <span>Messages:</span>
          {{ journey.timedMessages || 0 }}
        </p>
        <p class="journey-card__statistics__item">
          <span>Lessons:</span>
          {{ journey.lessons || 0 }}
        </p>
      </div>
      <div class="journey-card__print-actions">
        <div class="journey-card__status">
          <dot-status
            :status="{ done: completeJourneyPercentage >= 100 }"
            :completePercentage="completeJourneyPercentage"
            :progress="journey.progress || 0"
            :total="journey.total || 0"
          />
        </div>
        <v-btn
          icon
          class="journey-card__print-action-btn"
          color="accent"
          elevation="0"
          @click="$emit('print-journey', journey.id)"
        >
          <v-icon size="20">bhi-print</v-icon>
        </v-btn>
        <v-btn
          icon
          class="journey-card__delete-action-btn"
          color="accent"
          elevation="0"
          v-if="isCompanyUser"
          :disabled="isDeleting"
          :loading="isDeleting"
          @click="
            () => {
              handleDeleteJourney()
            }
          "
        >
          <v-icon size="20">bhi-trash</v-icon>
        </v-btn>
        <v-btn
          icon
          class="journey-card__show-description-btn"
          color="accent"
          elevation="0"
          :disabled="!journey.description"
          @click="toggleDisplayDescription"
        >
          <v-icon size="20">{{
            showDescription ? 'bhi-chevron-up' : 'bhi-chevron-down'
          }}</v-icon>
        </v-btn>
      </div>
    </div>
    <div
      v-if="journey.description && showDescription"
      :class="
        `journey-card__description ${
          showEndDate ? 'journey-card__description-pb' : ''
        }`
      "
    >
      <span>Description</span>
      <p>{{ journey.description }}</p>
    </div>
    <div
      class="journey-card__endDate"
      v-if="showEndDate"
      :class="generateEndDateClassBgColor"
    >
      <p class="journey-card__endDate--title">
        <span class="journey-card__endDate--icon">
          <i class="bhi-alert"></i>
        </span>
        End date Positive actions:
      </p>
      <p class="journey-card__endDate--value">
        {{ this.$moment(journey.endDate).format('MMM DD, YYYY') }}
        <span>{{ daysLeft }} {{ daysLeft === 1 ? 'day' : 'days' }} left</span>
      </p>
    </div>
  </div>
</template>

<script>
import DotStatus from '@/components/statuses/DotStatus.vue'
import { mapActions, mapState, mapGetters } from 'vuex'

export default {
  name: 'JourneyCard',
  components: {
    DotStatus,
  },
  data() {
    return {
      showDescription: false,
      isDeleting: false,
    }
  },
  props: {
    journey: {
      type: Object,
      required: true,
    },
    profileId: {
      type: Number,
    },
  },
  methods: {
    ...mapActions('journeysStates', ['deleteJourney', 'getJourneys']),
    async handleDeleteJourney() {
      if (this.isDeleting) return
      try {
        this.isDeleting = true
        await this.deleteJourney({
          profileId: this.profileId,
          journeyId: this.journey.id,
        })
      } finally {
        this.isDeleting = false
        this.getJourneys({ profileId: this.profileId })
      }
    },
    toggleDisplayDescription() {
      this.showDescription = !this.showDescription
    },
    formatDate(dateString) {
      if (!dateString) return ''
      return this.$moment(dateString).format('MMM DD, YYYY')
    },
  },
  computed: {
    ...mapState('dictionariesStates', ['dictionaryPracticeUserRole']),
    ...mapGetters('userStates', ['getRolename']),
    today() {
      return this.$moment().startOf('day')
    },
    daysCount() {
      return this.$moment(this.journey.endDate).diff(
        this.$moment(this.journey.startDate)
          .startOf('day')
          .subtract(1, 'day'),
        'days',
      )
    },
    daysLeft() {
      return this.$moment(this.journey.endDate).diff(
        this.$moment(this.today).subtract(1, 'day'),
        'days',
      )
    },
    generateEndDateClassBgColor() {
      if (this.daysLeft < 4) {
        return 'journey-card__endDate--red'
      }
      if (this.daysLeft > 7) {
        return 'journey-card__endDate--green'
      }
      return 'journey-card__endDate--yellow'
    },
    completeJourneyPercentage() {
      if (!this.journey.total || !this.journey.progress) return 0

      return Math.round((this.journey.progress / this.journey.total) * 100)
    },
    journeyLessons() {
      return this.journey.lessons || 0
    },
    journeyMessages() {
      return this.journey.timedMessages || 0
    },
    journeyPositiveActions() {
      return this.journey.positiveActions || 0
    },
    showEndDate() {
      return this.journey.endDate && this.daysLeft > 0
    },
    isCompanyUser() {
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ===
          this.dictionaryPracticeUserRole.Advisor ||
        this.dictionaryPracticeUserRole[this.getRolename] ===
          this.dictionaryPracticeUserRole.Administrator ||
        this.dictionaryPracticeUserRole[this.getRolename] ===
          this.dictionaryPracticeUserRole.Team
      )
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables';

$journey-card: '.journey-card';

.journey-card {
  border: 1px solid $bh-gray--5;
  border-radius: $border-radius-root2;
  padding: 16px;
  margin-bottom: 12px;
  &--in-progress {
    padding: 0;
    #{$journey-card} {
      &--wrap {
        padding: 16px;
      }

      &__description {
        padding: 0 16px 16px;
      }
    }
  }
  &--wrap {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
  &__name {
    font-size: 16px;
    line-height: 1.25;
    color: $bh-black;
    font-weight: 500;
    flex: 1;
    overflow: hidden;
    padding-right: $gutter;

    &--title {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      margin-bottom: 0 !important;
    }
  }
  &__time {
    font-size: 12px;
    line-height: 1;
    color: $bh-black;
    font-weight: 400;
    opacity: 0.6;
  }
  &__statistics {
    display: flex;
    flex-direction: row;
    gap: 16px;
    &__item {
      font-size: 12px;
      line-height: 1;
      color: $bh-black;
      font-weight: 400;
      margin-bottom: 0 !important;
      min-width: 120px;
      span {
        opacity: 0.6;
      }
    }
  }
  &__status {
    min-width: 170px;
  }
  &__endDate {
    display: flex;
    flex-direction: row;
    gap: 16px;
    background-color: $bh-yellow--light;
    border-radius: 0 0 $border-radius-root $border-radius-root;
    padding: 12px 24px;
    &--title,
    &--value {
      font-size: 12px;
      color: $bh-black;
      font-weight: 400;
      margin-bottom: 0 !important;
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 8px;
    }
    &--value {
      span {
        margin-left: 8px;
      }
    }
    &--icon {
      font-size: 16px;
      line-height: 1;
    }
    &--red {
      background-color: $bh-red--light;
    }
    &--yellow {
      background-color: $bh-yellow--light;
    }
    &--green {
      background-color: $bh-green--light;
    }
  }
  &__print-actions {
    display: flex;
    flex-direction: row;
    gap: 16px;
  }
  &__delete-action-btn,
  &__print-action-btn {
    min-width: 24px !important;
    width: 24px !important;
    height: 24px !important;
  }
  &__show-description-btn {
    min-width: 24px !important;
    width: 24px !important;
    height: 24px !important;
  }
  &__description {
    font-size: 16px;
    line-height: 1.35;
    color: $bh-black;
    font-weight: 400;
    margin-bottom: 0 !important;
    min-width: 120px;
    padding: 16px 0 0;
    white-space: pre-line;

    &-pb {
      padding-bottom: 16px;
    }

    p {
      margin-bottom: 0;
    }

    span {
      font-size: 12px;
      opacity: 0.6;
      display: block;
      margin-bottom: 0.5em;
    }
  }
}
</style>
